import { enumMembers, type ApiSchema } from "../apiSchema";
import { jsonBody, requestJson, withQuery } from "../http/httpClient";
import { readArray, readNullableString, readObject, readOneOf, readString, type JsonObject } from "../http/jsonDecoding";

/**
 * Asking for access (accounts.md GS-8, `/api/accounts/access-requests`): one asks for a permission
 * one lacks, where a refusal offers it; whoever could grant it sees the request with the groups it
 * could go through, the one adding least first, and grants it through one or declines it. A
 * request waits 30 days. The installation's `access_requests` switch turns asking off.
 */

export type AccessRequestState = ApiSchema<"AccessRequestState">;
const REQUEST_STATES = enumMembers<AccessRequestState>({ pending: true, granted: true, declined: true, withdrawn: true, expired: true });

export interface AccessRequest {
  readonly requestId: string;
  /** `null` for an installation permission. */
  readonly projectId: string | null;
  /** The grant as a refusal's remedy names it: `propose`, `curate:<item kind>`. */
  readonly needs: string;
  readonly reason: string;
  readonly state: AccessRequestState;
  /** ISO 8601. */
  readonly requestedAt: string;
}

/** A grant one group would add, in a project or (`null`) the installation. */
export interface ScopedNeed {
  readonly projectId: string | null;
  readonly needs: string;
}

/** A group a request can be granted through, with everything joining it adds. */
export interface RequestOffer {
  readonly groupId: string;
  readonly name: string;
  readonly adds: readonly ScopedNeed[];
}

export interface DecidableRequest {
  readonly request: AccessRequest;
  readonly requesterName: string;
  /** The one adding least first. */
  readonly offers: readonly RequestOffer[];
}

export function askForAccess(projectId: string | null, needs: string, reason: string): Promise<AccessRequest> {
  const body = { project_id: projectId, needs, reason } satisfies ApiSchema<"AskBody">;
  return requestJson({ method: "POST", path: "/accounts/access-requests", body: jsonBody(body), decodeResponse: decodeRequest });
}

/** One's own latest requests, newest first. */
export function listOwnRequests(signal?: AbortSignal): Promise<readonly AccessRequest[]> {
  return requestJson({
    method: "GET",
    path: "/accounts/access-requests/mine",
    signal,
    decodeResponse: (body) => readArray(body, "access requests", decodeRequest),
  });
}

/** The waiting requests in a project, or (`null`) at the installation, that one may grant. */
export function listRequestsToDecide(projectId: string | null, signal?: AbortSignal): Promise<readonly DecidableRequest[]> {
  return requestJson({
    method: "GET",
    path: withQuery("/accounts/access-requests/to-decide", { project_id: projectId }),
    signal,
    decodeResponse: (body) => readArray(body, "requests to decide", (item) => decodeDecidable(readObject(item, "request to decide"))),
  });
}

export function grantRequest(requestId: string, groupId: string): Promise<AccessRequest> {
  const body = { group_id: groupId } satisfies ApiSchema<"GrantThroughBody">;
  return requestJson({ method: "POST", path: `${requestPath(requestId)}/grant`, body: jsonBody(body), decodeResponse: decodeRequest });
}

export function declineRequest(requestId: string): Promise<AccessRequest> {
  return requestJson({ method: "POST", path: `${requestPath(requestId)}/decline`, decodeResponse: decodeRequest });
}

export function withdrawRequest(requestId: string): Promise<AccessRequest> {
  return requestJson({ method: "POST", path: `${requestPath(requestId)}/withdraw`, decodeResponse: decodeRequest });
}

function requestPath(requestId: string): `/${string}` {
  return `/accounts/access-requests/${encodeURIComponent(requestId)}`;
}

function decodeRequest(value: unknown): AccessRequest {
  const request = readObject(value, "access request");
  return {
    requestId: readString(request, "id"),
    projectId: readNullableString(request, "project_id"),
    needs: readString(request, "needs"),
    reason: readString(request, "reason"),
    state: readOneOf(request, "state", REQUEST_STATES),
    requestedAt: readString(request, "requested_at"),
  };
}

function decodeDecidable(decidable: JsonObject): DecidableRequest {
  return {
    request: decodeRequest(decidable["request"]),
    requesterName: readString(decidable, "requester_name"),
    offers: readArray(decidable["offers"], "offers", (item) => {
      const offer = readObject(item, "offer");
      return {
        groupId: readString(offer, "group_id"),
        name: readString(offer, "name"),
        adds: readArray(offer["adds"], "what it adds", (added) => {
          const need = readObject(added, "added grant");
          return { projectId: readNullableString(need, "project_id"), needs: readString(need, "needs") };
        }),
      };
    }),
  };
}
